import React, { useMemo, useRef, useState } from 'react'
import {
  IAnomaliesV2Alert,
  IAnomaliesV2RelatedAlert,
  INetworksV2Networks,
  TAnomaliesAlertFamily,
  TAnomaliesDevice,
  TAnomaliesPrimaryMetric,
} from '../shared/types'
import { useApiGet, useOutsideClick } from '../shared'

// Small uppercase label that sits above a cluster of filter controls. Pulls
// the eye to the purpose of each group on the filter bar without taking
// much vertical space.
export const FilterGroup = (props: {
  label: string
  grow?: boolean
  children: React.ReactNode
}) => (
  <div
    className="anomalies-filter-group"
    style={props.grow ? { flex: 1, minWidth: 0 } : undefined}
  >
    <div className="anomalies-filter-group-label">{props.label}</div>
    <div className="anomalies-filter-group-body">{props.children}</div>
  </div>
)

// ----- Network scope filter ------------------------------------------------
// Four-option pill shared by daily triage and rejection review. Three presets
// (All / Direct / Affiliate) cover the common cases; the fourth (Custom)
// opens a popover with the full network list for ad-hoc selection. Backend
// accepts `include_networks` / `exclude_networks` as UUID lists, so the FE
// sends whichever list is shorter — never more than ~half the network count.
export type NetworkScope = 'all' | 'direct' | 'affiliate' | 'custom'

export const NETWORK_SCOPE_LABELS: Record<NetworkScope, string> = {
  all: 'All networks',
  direct: 'Direct',
  affiliate: 'Affiliate',
  custom: 'Custom',
}

// Heylink Inbound's `networkIdentifier` is stable across dev / staging / prod
// (the UUID isn't), so we look up by identifier and return whatever UUID the
// current env exposes.
const HEYLINK_INBOUND_IDENTIFIER = 'heylinkinbound'

export interface AnomaliesNetworksState {
  // Full list of networks for the picker (sorted by name). Null until the
  // /networks/v2/networks call resolves.
  networks: INetworksV2Networks[] | null
  // Heylink Inbound UUID for the current env, or null if not yet loaded or
  // unexpectedly missing.
  heylinkUuid: string | null
}

// Single hook used by both pages. Caches via useApiGet so we only fetch
// networks once per session.
export const useAnomaliesNetworks = (): AnomaliesNetworksState => {
  const data = useApiGet<INetworksV2Networks[]>('/networks/v2/networks')
  return useMemo(() => {
    if (!data) return { networks: null, heylinkUuid: null }
    const sorted = [...data].sort((a, b) =>
      (a.networkName || '').localeCompare(b.networkName || '')
    )
    const match = sorted.find(
      (n) => n.networkIdentifier === HEYLINK_INBOUND_IDENTIFIER
    )
    return { networks: sorted, heylinkUuid: match?.networkUuid ?? null }
  }, [data])
}

// Translate the pill state into request params. Mutates and returns the
// passed-in object so it composes with the existing param-building idiom.
//
// Wire-shape rule: send the shorter of (selected set) or (deselected set).
// For Custom that means we send either `include_networks=...` when the user
// picked few, or `exclude_networks=...` when they picked most — the wire
// never carries more than ~half the total network count.
//
// If networks haven't loaded yet, we silently fall back to "all" — better
// than sending a malformed param.
export const applyNetworkScopeParams = (
  params: Record<string, string | number>,
  scope: NetworkScope,
  heylinkUuid: string | null,
  customUuids?: string[],
  allNetworks?: INetworksV2Networks[] | null
): Record<string, string | number> => {
  if (scope === 'all') return params
  if (scope === 'direct') {
    if (heylinkUuid) params.include_networks = heylinkUuid
    return params
  }
  if (scope === 'affiliate') {
    if (heylinkUuid) params.exclude_networks = heylinkUuid
    return params
  }
  // custom
  if (!allNetworks || !customUuids) return params
  const total = allNetworks.length
  const selected = customUuids.filter((uuid) =>
    allNetworks.some((n) => n.networkUuid === uuid)
  )
  // 0 or full selection → no filter (treat as All to avoid an empty page).
  if (selected.length === 0 || selected.length === total) return params
  if (selected.length <= total / 2) {
    params.include_networks = selected.join(',')
  } else {
    const unselected = allNetworks
      .filter((n) => !selected.includes(n.networkUuid))
      .map((n) => n.networkUuid)
    params.exclude_networks = unselected.join(',')
  }
  return params
}

// Pre-populate the Custom set when the user switches into Custom from a
// preset. Lets them refine instead of rebuild.
export const customUuidsForPreset = (
  scope: NetworkScope,
  heylinkUuid: string | null,
  allNetworks: INetworksV2Networks[] | null
): string[] => {
  if (!allNetworks) return []
  if (scope === 'direct' && heylinkUuid) return [heylinkUuid]
  if (scope === 'affiliate' && heylinkUuid) {
    return allNetworks
      .filter((n) => n.networkUuid !== heylinkUuid)
      .map((n) => n.networkUuid)
  }
  // 'all' or unknown → start with everything selected
  return allNetworks.map((n) => n.networkUuid)
}

// Shared segmented control. Four options; the Custom button shows a count
// when populated and opens a popover with the full network list.
export const NetworkScopePill = (props: {
  value: NetworkScope
  onChange: (next: NetworkScope) => void
  customUuids: string[]
  onCustomUuidsChange: (next: string[]) => void
}) => {
  const { value, onChange, customUuids, onCustomUuidsChange } = props
  const { networks, heylinkUuid } = useAnomaliesNetworks()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  useOutsideClick(wrapRef, () => setOpen(false))

  const handlePreset = (next: NetworkScope) => {
    if (next === 'custom') {
      // Switching INTO custom: seed from whatever preset was just active so
      // the operator refines a sensible starting set rather than an empty one.
      if (value !== 'custom') {
        onCustomUuidsChange(
          customUuidsForPreset(value, heylinkUuid, networks)
        )
      }
      onChange('custom')
      setOpen(true)
      return
    }
    onChange(next)
    setOpen(false)
  }

  const customCount =
    value === 'custom' && networks
      ? customUuids.filter((u) => networks.some((n) => n.networkUuid === u))
          .length
      : 0

  const options: NetworkScope[] = ['all', 'direct', 'affiliate', 'custom']

  return (
    <div className="anomalies-network-scope-wrap" ref={wrapRef}>
      <div className="anomalies-rejection-review-view-toggle">
        {options.map((s) => (
          <button
            key={s}
            type="button"
            className={
              'anomalies-rejection-review-view-toggle-btn' +
              (value === s ? ' active' : '')
            }
            onClick={() => handlePreset(s)}
          >
            {s === 'custom' && customCount > 0
              ? `Custom · ${customCount}`
              : NETWORK_SCOPE_LABELS[s]}
          </button>
        ))}
      </div>

      {open && value === 'custom' && (
        <CustomNetworkPicker
          networks={networks}
          selectedUuids={customUuids}
          onChange={onCustomUuidsChange}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  )
}

// Popover shown below the pill when the operator is in Custom mode. Plain
// checkbox list with a search field at the top. No "include/exclude" choice
// — operators just say "show these"; the FE figures out the smaller of
// (selected, deselected) to send over the wire.
const CustomNetworkPicker = (props: {
  networks: INetworksV2Networks[] | null
  selectedUuids: string[]
  onChange: (next: string[]) => void
  onClose: () => void
}) => {
  const { networks, selectedUuids, onChange, onClose } = props
  const [query, setQuery] = useState('')
  const selectedSet = useMemo(() => new Set(selectedUuids), [selectedUuids])

  const visible = useMemo(() => {
    if (!networks) return []
    const q = query.trim().toLowerCase()
    if (!q) return networks
    return networks.filter((n) =>
      (n.networkName || '').toLowerCase().includes(q)
    )
  }, [networks, query])

  const toggle = (uuid: string) => {
    const next = new Set(selectedSet)
    if (next.has(uuid)) next.delete(uuid)
    else next.add(uuid)
    onChange(Array.from(next))
  }

  const selectAll = () => {
    if (!networks) return
    onChange(networks.map((n) => n.networkUuid))
  }
  const selectNone = () => onChange([])

  return (
    <div className="anomalies-network-picker">
      <div className="anomalies-network-picker-header">
        <input
          type="text"
          className="anomalies-network-picker-search"
          placeholder="Search networks…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="row row-center" style={{ gap: 6 }}>
          <button
            type="button"
            className="anomalies-faq-toggle"
            onClick={selectAll}
          >
            All
          </button>
          <button
            type="button"
            className="anomalies-faq-toggle"
            onClick={selectNone}
          >
            None
          </button>
        </div>
      </div>
      <div className="anomalies-network-picker-list">
        {networks == null ? (
          <div className="text-light text-center p-y-6">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-light text-center p-y-6">
            No networks match “{query}”.
          </div>
        ) : (
          visible.map((n) => (
            <label
              key={n.networkUuid}
              className="anomalies-network-picker-row"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(n.networkUuid)}
                onChange={() => toggle(n.networkUuid)}
              />
              <span>{n.networkName || n.networkUuid}</span>
            </label>
          ))
        )}
      </div>
      <div className="anomalies-network-picker-footer">
        <span className="text-light text-small">
          {selectedSet.size} of {networks?.length ?? 0} selected
        </span>
        <button
          type="button"
          className="anomalies-faq-toggle"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  )
}

// Family + metric color palette. Red = acute, amber = drift, blue/grey =
// rejection. Used for badges, chart strokes and small accents.
export const FAMILY_COLORS: Record<TAnomaliesAlertFamily, string> = {
  acute: '#E55353',
  drift: '#EBB617',
  rejection: '#4F9EEC',
}

export const FAMILY_LABELS: Record<TAnomaliesAlertFamily, string> = {
  acute: 'Acute',
  drift: 'Drift',
  rejection: 'Rejection',
}

export const METRIC_LABELS: Record<TAnomaliesPrimaryMetric, string> = {
  cr: 'Conversion rate',
  epc: 'EPC',
  spc: 'Sale per click',
  clicks: 'Clicks',
  conversions: 'Conversions',
  rejection_rate: 'Rejection rate',
}

// Whether the metric is rendered as a percentage (rate) vs a DKK value vs an
// integer count. Drives both formatting and Y-axis tick formatting.
export type TMetricUnit = 'percent' | 'currency' | 'number'

export const METRIC_UNIT: Record<TAnomaliesPrimaryMetric, TMetricUnit> = {
  cr: 'percent',
  epc: 'currency',
  spc: 'currency',
  clicks: 'number',
  conversions: 'number',
  rejection_rate: 'percent',
}

// Display label per (family, primary_metric). Falls back to the metric label
// alone when no specific override exists.
const FAMILY_METRIC_LABELS: Record<string, string> = {
  'drift|cr': 'Conversion rate drift',
  'drift|epc': 'EPC / commission drift',
  'drift|spc': 'Sale-per-click drift',
  'acute|cr': 'CR acute drop',
  'acute|epc': 'EPC acute drop',
  'acute|spc': 'Sale-per-click acute drop',
  'acute|clicks': 'Clicks dropped',
  'acute|conversions': 'Conversions dropped',
  'rejection|rejection_rate': 'Rejection rate spike',
}

// "Commission missing" is the canonical name for an EPC drift where commission
// has gone to zero while sales continue — it's the single most-actionable
// signal (tracking broken), so it's surfaced even when the API didn't echo
// the special displayReason.
export const isCommissionMissing = (alert: IAnomaliesV2Alert): boolean =>
  (alert.alertFamily === 'drift' && alert.primaryMetric === 'epc') &&
  (
    alert.reasonText?.startsWith('Commission missing') ||
    alert.displayReason === 'Commission missing' ||
    alert.currentValue === 0
  )

export const displayLabelFor = (alert: IAnomaliesV2Alert): string => {
  if (isCommissionMissing(alert)) return 'Commission missing'
  if (alert.displayReason) return alert.displayReason
  const key = `${alert.alertFamily}|${alert.primaryMetric}`
  return FAMILY_METRIC_LABELS[key] || METRIC_LABELS[alert.primaryMetric]
}

// Short uppercased metric token for evidence-card labels: "Current 30d SPC"
// reads more cleanly than "Current 30d Sale per click".
export const METRIC_SHORT: Record<TAnomaliesPrimaryMetric, string> = {
  cr: 'CR',
  epc: 'EPC',
  spc: 'SPC',
  clicks: 'Clicks',
  conversions: 'Conversions',
  rejection_rate: 'Rejection rate',
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

// European number formatting — `.` as thousand separator, `,` as decimal
// mark. Hand-rolled (not Intl.NumberFormat) on purpose: relying on a locale
// string means Chrome / Node ICU choices can quietly fall back to en-US for
// browsers whose UI language is English, producing `1,234.5` instead of
// `1.234,5`. This version is deterministic everywhere.
const formatEuropean = (value: number, digits: number): string => {
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  const fixed = abs.toFixed(digits)
  const [intPart, decPart] = fixed.split('.')
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return decPart ? `${sign}${withThousands},${decPart}` : `${sign}${withThousands}`
}

// Locale-aware decimal formatter — use instead of `.toFixed(n)` anywhere the
// number is shown to the user, so the thousands separator + decimal mark
// match the rest of the app.
export const formatDecimal = (
  value: number | null | undefined,
  digits: number
): string => {
  if (value == null || isNaN(value)) return '—'
  return formatEuropean(value, digits)
}

export const formatPercent = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—'
  return `${formatEuropean(value * 100, 1)}%`
}

export const formatDkkPrecise = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—'
  if (value === 0) return '0 DKK'
  return `${formatEuropean(value, 2)} DKK`
}

export const formatDkkLarge = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—'
  return `${formatEuropean(value, 0)} DKK`
}

export const formatInteger = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—'
  return formatEuropean(Math.round(value), 0)
}

export const formatScore = (value: number | null | undefined): string => {
  if (value == null || isNaN(value)) return '—'
  return formatEuropean(value, 1)
}

// Some backend strings come pre-formatted with en-US separators (e.g.
// `estimatedLossLabel: "Est. 42,563 DKK lost sale"`). The FE can't undo the
// formatting cleanly without reparsing the number, but we can swap separators
// in-place: comma between digits → period, period between digits → comma.
// Safe on strings that contain no numbers (returns them unchanged).
export const europeanizeNumbers = (s: string | null | undefined): string => {
  if (!s) return ''
  return s.replace(/(\d)([.,])(?=\d)/g, (_m, digit, sep) =>
    sep === ',' ? `${digit}.` : `${digit},`
  )
}

export const formatPercentChange = (
  value: number | null | undefined
): string => {
  if (value == null || isNaN(value)) return '—'
  const pct = Math.round(value * 100)
  return `${pct > 0 ? '+' : ''}${pct}%`
}

export const formatMetricValue = (
  value: number | null | undefined,
  metric: TAnomaliesPrimaryMetric
): string => {
  const unit = METRIC_UNIT[metric]
  if (unit === 'percent') return formatPercent(value)
  if (unit === 'currency') return formatDkkPrecise(value)
  return formatInteger(value)
}

// Y-axis ticks need a compact form ("3.4%", "0.31", "12k") — full DKK suffix
// would clutter the chart axis.
export const tickFormatterFor =
  (unit: TMetricUnit) =>
  (v: number): string => {
    if (v == null || isNaN(v)) return ''
    if (unit === 'percent') return `${formatEuropean(v * 100, 1)}%`
    if (unit === 'currency') return formatEuropean(v, 2)
    if (Math.abs(v) >= 1000) return `${formatEuropean(v / 1000, 1)}k`
    return formatEuropean(Math.round(v), 0)
  }

// ---------------------------------------------------------------------------
// Badges (NEW / WORSENING / ACTIVE Xd)
// ---------------------------------------------------------------------------

export interface BadgeSpec {
  label: string
  variant: 'new' | 'worsening' | 'active'
}

// Visual hierarchy: NEW outranks everything; otherwise WORSENING leads, ACTIVE
// follows. NEW alerts skip ACTIVE since they're by definition day 1.
export const badgesFor = (alert: IAnomaliesV2Alert): BadgeSpec[] => {
  if (alert.isFirstAlertDay) {
    return [{ label: 'NEW', variant: 'new' }]
  }
  const out: BadgeSpec[] = []
  if (alert.isWorseningToday) {
    out.push({ label: 'WORSENING', variant: 'worsening' })
  }
  if (alert.alertDaysActive > 1) {
    out.push({
      label: `ACTIVE ${alert.alertDaysActive}d`,
      variant: 'active',
    })
  }
  return out
}

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200]

// Convert a current/anchor ratio to user-facing copy. ratio<1 reads "X%
// below", ratio>1 reads "X% above", and ~1 reads "in line with anchor".
// Returns null when the ratio is missing so the caller can decide whether
// to render at all.
export const formatVsAnchor = (ratio: number | null | undefined): string | null => {
  if (ratio == null || isNaN(ratio)) return null
  const pct = Math.round((1 - ratio) * 100)
  if (Math.abs(pct) < 1) return 'in line with baseline'
  if (pct > 0) return `${pct}% below baseline`
  return `${Math.abs(pct)}% above baseline`
}

// Robust upper bound for chart y-axis when "Normal range" is selected. P95
// keeps a few peaks visible without letting a single Q4/BF spike crush the
// rest of the series. Returns null when there isn't enough data.
export const robustYMax = (values: (number | null | undefined)[]): number | null => {
  const cleaned = values
    .filter((v): v is number => v != null && !isNaN(v))
    .sort((a, b) => a - b)
  if (cleaned.length === 0) return null
  const idx = Math.min(cleaned.length - 1, Math.floor(cleaned.length * 0.95))
  return cleaned[idx]
}

// Small "?" icon that reveals a tooltip on hover. CSS-driven (no JS state) so
// it's cheap to scatter across cards/headers. Pass a ReactNode for tooltip
// content when multi-line copy is needed; bare strings work too.
export const HelpIcon = (props: { tooltip: React.ReactNode }) => (
  <span className="anomalies-help-icon" tabIndex={0}>
    ?
    <span className="anomalies-help-tooltip">{props.tooltip}</span>
  </span>
)

// Grouped period of consecutive raw alert rows sharing a single
// (device, family, metric) signal. Built frontend-side from the raw alert
// lists so the related/history panels don't show one row per day for a
// continuous drift signal.
export interface AlertPeriod {
  device: TAnomaliesDevice
  alertFamily: TAnomaliesAlertFamily
  primaryMetric: TAnomaliesPrimaryMetric
  startDate: string
  endDate: string
  alertDays: number
  maxPriorityScore: number
  latestPriorityScore: number
  representativeReason: string
  rows: IAnomaliesV2RelatedAlert[]
}

const oneDayApart = (a: string, b: string): boolean => {
  // Strict consecutive-day check on YYYY-MM-DD strings — a is the earlier
  // day, b is the next one.
  try {
    const ad = new Date(a + 'T00:00:00Z').getTime()
    const bd = new Date(b + 'T00:00:00Z').getTime()
    return bd - ad === 86400000
  } catch {
    return false
  }
}

// Given a raw alert list, collapse runs of consecutive same-key days into
// periods. Sorts by date ASC inside each group; periods themselves are
// returned newest-first so the most recent activity is at the top.
export const groupAlertPeriods = (
  rows: IAnomaliesV2RelatedAlert[]
): AlertPeriod[] => {
  const buckets = new Map<string, IAnomaliesV2RelatedAlert[]>()
  for (const row of rows) {
    const key = `${row.device}|${row.alertFamily}|${row.primaryMetric}`
    const list = buckets.get(key) || []
    list.push(row)
    buckets.set(key, list)
  }

  const periods: AlertPeriod[] = []
  for (const list of buckets.values()) {
    list.sort((a, b) => a.eventDate.localeCompare(b.eventDate))
    let run: IAnomaliesV2RelatedAlert[] = []
    const flush = () => {
      if (run.length === 0) return
      const first = run[0]
      const last = run[run.length - 1]
      const maxScore = run.reduce(
        (m, r) => Math.max(m, r.priorityScore ?? 0),
        0
      )
      // Pick the day with the highest score to represent the period in
      // compact UI — that's typically the worst day's reason text.
      const worst = run.reduce(
        (best, r) =>
          (r.priorityScore ?? 0) > (best.priorityScore ?? 0) ? r : best,
        first
      )
      periods.push({
        device: first.device,
        alertFamily: first.alertFamily,
        primaryMetric: first.primaryMetric,
        startDate: first.eventDate,
        endDate: last.eventDate,
        alertDays: run.length,
        maxPriorityScore: maxScore,
        latestPriorityScore: last.priorityScore ?? 0,
        representativeReason:
          worst.reasonText || worst.displayReason || '',
        rows: run.slice(),
      })
      run = []
    }
    for (const row of list) {
      if (run.length === 0) {
        run.push(row)
      } else if (oneDayApart(run[run.length - 1].eventDate, row.eventDate)) {
        run.push(row)
      } else {
        flush()
        run.push(row)
      }
    }
    flush()
  }

  // Newest-first: sort by endDate desc, then maxScore desc as tie-breaker.
  return periods.sort((a, b) => {
    const cmp = b.endDate.localeCompare(a.endDate)
    if (cmp !== 0) return cmp
    return b.maxPriorityScore - a.maxPriorityScore
  })
}
